import { Component, computed, effect, HostListener, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import {GeolocationService} from './services/geolocation.service';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatSidenavModule} from '@angular/material/sidenav';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {AuthService} from '@lib-ui/auth/auth.service';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerInputEvent,
    MatDatepickerToggle
} from '@angular/material/datepicker';
import {DateService} from '@executor/src/app/services/date.service';
import {DatePipe, DOCUMENT} from '@angular/common';
import { MessageService } from '@lib-services/message.service';
import { OfflineService } from '@lib-ui/offline/offline.service';
import { OfflineFilesService } from '@lib-ui/offline/offliene.files.service';
import { switchMap } from 'rxjs';
import { OfflineStorageService } from '@lib-ui/offline/OfflineStorageService.service';
import { MatDialog } from '@angular/material/dialog';
import { FormCashMachineComponent } from '@executor/src/app/components/form-cash-machine/form-cash-machine.component';

@Component({
    standalone: true,
    imports: [
        RouterOutlet,
        MatIconModule,
        MatSidenavModule,
        MatToolbarModule,
        MatButtonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        FormsModule,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        MatDatepickerToggle,
        MatDatepicker,
        MatDatepickerInput,
        DatePipe,
    ],
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
    refreshLocationPeriod = 1; // в минутах
    @ViewChild('picker', { static: false }) datepicker!: MatDatepicker<any>;
    private intervalId!: ReturnType<typeof setInterval> | null;

    document = inject(DOCUMENT);

    progress = computed(() => {
        const calculateProgress = this.offlineService.calculateProgress(this.offlineService.progressBarLoadingData()).toFixed();
        return Number(calculateProgress);
    });

    constructor(
        protected authService: AuthService,
        private geolocationService: GeolocationService,
        private router: Router,
        protected dateService: DateService,
        public offlineService: OfflineService,
        private dialog: MatDialog,
        private messageService: MessageService,
        private offlineFilesService: OfflineFilesService,
        private offlineStorageService: OfflineStorageService
    ) {
        effect(() => {
            if (authService.user().data.id) {
                this.startInterval();
            }
        });
    }

    @HostListener('window:online') onOnline(): void {
        this.offlineService.offline.set(false);
        this.startInterval();
        this.onRetryRequests();
    }

    @HostListener('window:offline') onOffline(): void {
        this.offlineService.offline.set(true);
        this.messageService.errorMessage('Отсутствует подключение к интернету');
        this.stopInterval();
    }

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: BeforeUnloadEvent): void {
        if (this.offlineService.offline()) {
            event.preventDefault();
        }
    }

    ngOnInit(): void {
        this.authService.authCheck();

        if (this.document.defaultView && this.document.defaultView.navigator.onLine) {
            this.offlineService.offline.set(!this.document.defaultView.navigator.onLine);
        }

        if (!this.offlineService.offline()) {
            this.offlineService.loadDateOrderOffline$.pipe(
                switchMap(() => this.offlineService.onSaveDataOfflineOrder()),
            ).subscribe({
                next: (res) => {
                    if (res) {
                        this.messageService.successMessage('Данные успешно загружены для offline режима');
                    }
                },
                error: () => {
                    this.messageService.errorMessage('Ошибка при загрузки данных для offline режима');
                }
            });
        }

    }

    ngOnDestroy(): void {
        this.stopInterval();
    }

    openDatePicker(): void {
        this.datepicker.open();
    }

    startInterval(): void {
        this.intervalId = setInterval(() => this.geolocationService.getGeoLocation(), this.refreshLocationPeriod * 60 * 1000);
    }

    stopInterval(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    addCashMachine(): void {
        this.dialog.open(FormCashMachineComponent, {
            width: '95%',
            maxWidth: '600px',
        });
    }

    async onRetryRequests(): Promise<void> {
        const dataFile = await this.offlineFilesService.getOfflineFiles();
        this.offlineStorageService.retryRequest(dataFile).subscribe({});
    }

    onDateChange(event: MatDatepickerInputEvent<any>): void {
        this.dateService.currentDate.set(event.value);
    }

    goToOrdersList(): void {
        this.router.navigate(['orders-list']);
    }

    logout(): void {
        if (this.offlineService.offline()) {
            this.messageService.errorMessage('Отсутствует подключение к интернету');
            return;
        }

        this.authService.logout();
    }

    get hasCashMachine(): boolean {
        return !!this.authService.user().data && !!this.authService.user().data?.company?.settings?.paymentDevice;
    }
}
